'use client';

import React, { useEffect, useRef } from 'react';

interface ScheduleEvent {
  time?: string;
  label: string;
}

interface ScheduleDay {
  date: string;
  tithi: string;
  label: string;
  pujaEvents: ScheduleEvent[];
  otherEvents: string[];
  icon: string;
  isGala: boolean;
}

// Puja timings sourced verbatim from HCAF_Durga_Puja_2026_Schedule_Event_Wise_Timings.xlsx
const schedule: ScheduleDay[] = [
  {
    date: 'Thu, 15 Oct',
    tithi: 'Maha Panchami',
    label: 'Day 0',
    pujaEvents: [],
    otherEvents: ['Closed Door Ceremony'],
    icon: '🪔',
    isGala: false,
  },
  {
    date: 'Fri, 16 Oct',
    tithi: 'Maha Shoshthi',
    label: 'Day 1',
    pujaEvents: [
      { time: '9:00 AM onwards', label: 'Shoshthi Puja' },
      { time: '10:00 AM', label: 'Pushpanjali' },
      { time: '6:00 PM', label: 'Sandhya Aaroti' },
      { time: '6:00 PM onwards', label: 'Bodhan + Abhibaas' },
    ],
    otherEvents: ['Anondo Mela Opens - Community Food Bazaar', 'Cultural Evening Programme'],
    icon: '🍛',
    isGala: false,
  },
  {
    date: 'Sat, 17 Oct',
    tithi: 'Maha Saptami',
    label: 'Day 2',
    pujaEvents: [
      { time: '7:15 AM – 8:04 AM', label: 'Nabapatrika Snaan' },
      { time: '9:00 AM', label: 'Pushpanjali' },
      { time: '6:30 PM', label: 'Sandhya Aaroti' },
    ],
    otherEvents: ['Sit-Down Bhog Distribution', 'Cultural Programme by HCAF Members'],
    icon: '🌸',
    isGala: false,
  },
  {
    date: 'Sun, 18 Oct',
    tithi: 'Maha Ashtami Day 1',
    label: 'Day 3',
    pujaEvents: [
      { time: '8:00 AM', label: 'Ashtami Puja' },
      { time: '12:30 PM onwards', label: 'Pushpanjali' },
      { time: '6:30 PM', label: 'Sandhya Aaroti' },
    ],
    otherEvents: ['Sit-Down Bhog Distribution', 'Cultural Programme by HCAF Members'],
    icon: '🙏',
    isGala: false,
  },
  {
    date: 'Mon, 19 Oct',
    tithi: 'Maha Ashtami Day 2',
    label: 'Day 4',
    pujaEvents: [
      { time: '7:25 AM – 8:15 AM', label: 'Sandhi Puja' },
      { time: '9:00 AM onwards', label: 'Pushpanjali' },
      { time: '10:00 AM onwards', label: 'Kumari Puja' },
      { time: '6:30 PM', label: 'Sandhya Aaroti' },
    ],
    otherEvents: ['Sit-Down Bhog Distribution', 'Drama: "Byapika Bidai"', 'Cultural Evening'],
    icon: '🎭',
    isGala: false,
  },
  {
    date: 'Tue, 20 Oct',
    tithi: 'Maha Nabami',
    label: 'Day 5',
    pujaEvents: [
      { time: '6:00 AM – 7:30 AM', label: 'Nabami Puja / Yagya' },
      { time: '8:00 AM onwards', label: 'Yagya' },
      { time: '11:00 AM onwards', label: 'Pushpanjali' },
      { time: '6:30 PM', label: 'Sandhya Aaroti' },
      { time: '7:00 PM onwards', label: 'GALA NIGHT - Javed Ali live at Shanmukhananda Chandrasekhara Saraswati Auditorium' },
    ],
    otherEvents: [],
    icon: '🎵',
    isGala: true,
  },
  {
    date: 'Wed, 21 Oct',
    tithi: 'Maha Dashami',
    label: 'Day 6',
    pujaEvents: [
      { time: '8:00 AM onwards', label: 'Puja' },
      { time: '10:47 AM', label: 'Darpan Bisarjan' },
      { time: '11:45 AM onwards', label: 'Sindoor Khela' },
    ],
    otherEvents: ['Bijoya Sammelan', 'Visarjan (Immersion) & Farewell'],
    icon: '🌺',
    isGala: false,
  },
];

export default function ScheduleSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('active'); }); },
      { threshold: 0.05 }
    );
    sectionRef?.current?.querySelectorAll('.reveal')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-background w-full px-4 md:px-12 pt-14 md:pt-20 pb-12 md:pb-16">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 md:mb-10 reveal text-center">
          <span className="inline-block px-4 py-1.5 rounded-full border border-border text-muted-foreground text-xs font-bold tracking-widest uppercase mb-4 md:mb-5">
            Full Schedule
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black text-foreground tracking-tight">
            Prathaa <span className="text-primary">2026</span>
          </h2>
          <p className="text-muted-foreground text-xs md:text-sm mt-3">
            Debi Aagomon: Ghotoke · Debi Gomon: Nauka-e
          </p>
        </div>

        <div className="flex flex-col gap-4 md:gap-5">
          {schedule.map((day, i) => (
            <div
              key={day.date}
              className={`reveal bg-card border rounded-3xl p-6 md:p-8 relative overflow-hidden transition-all duration-300 ${
                day.isGala ? 'border-primary/30 bg-gradient-to-br from-primary/5 to-transparent' : 'border-border'
              }`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className="flex flex-wrap items-center gap-3 mb-5 md:mb-6">
                <span className="text-2xl md:text-3xl">{day.icon}</span>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-xl md:text-2xl font-black text-foreground">{day.date}</h3>
                    {day.isGala && (
                      <span className="px-2.5 py-0.5 rounded-full bg-primary text-primary-foreground text-xs font-bold tracking-widest uppercase animate-pulse-glow">
                        Gala Night
                      </span>
                    )}
                  </div>
                  <p className="text-muted-foreground text-xs font-semibold tracking-widest uppercase mt-0.5">{day.tithi} · {day.label}</p>
                </div>
              </div>

              {day.pujaEvents.length > 0 && (
                <div className={day.otherEvents.length > 0 ? 'mb-5 md:mb-6' : ''}>
                  <p className="text-accent text-xs font-bold tracking-widest uppercase mb-3">Puja Timings</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 divide-y divide-border sm:divide-y-0">
                    {day.pujaEvents.map((evt) => (
                      <div key={`${evt.time}-${evt.label}`} className="flex items-start gap-3 py-2 sm:py-0">
                        <span className="flex-shrink-0 w-28 md:w-32 text-sm font-bold text-accent">
                          {evt.time}
                        </span>
                        <span className={`text-sm ${day.isGala && evt.label.includes('GALA') ? 'text-primary font-bold' : 'text-card-foreground/90'}`}>
                          {evt.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {day.otherEvents.length > 0 && (
                <div>
                  <p className="text-muted-foreground text-xs font-bold tracking-widest uppercase mb-3">
                    {day.pujaEvents.length > 0 ? 'Also Today' : 'Programme'}
                  </p>
                  <ul className="space-y-2">
                    {day.otherEvents.map((label) => (
                      <li key={label} className="flex items-start gap-2 text-sm text-card-foreground/80">
                        <span className="text-accent/60 mt-1.5 flex-shrink-0">✦</span>
                        {label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
